import React from 'react';
import './AdminPayoutStatusModal.css';

function formatKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const getStatusColor = (status: string): string => {
  const s = (status || '').toUpperCase();
  if (s === 'SUCCESS' || s === 'APPROVED' || s === 'COMPLETED') return '#10B981';
  if (s === 'FAILED' || s === 'ERROR') return '#EF4444';
  if (s === 'PENDING' || s === 'PROCESSING') return '#F59E0B';
  return '#6B7280';
};

export interface ResponseMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Full response_data (object or parsed JSON) or error_message string */
  data: string | object | null;
}

const ResponseMessageModal: React.FC<ResponseMessageModalProps> = ({ isOpen, onClose, data }) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  if (data == null) {
    return (
      <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
        <div className="admin-payout-status-modal" onClick={(e) => e.stopPropagation()}>
          <div className="admin-payout-status-header">
            <h2 className="admin-payout-status-title">Response Message</h2>
            <button type="button" className="admin-payout-status-close" onClick={onClose} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="admin-payout-status-content" style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: '#6b7280', margin: 0 }}>No data</p>
          </div>
          <div className="admin-payout-status-footer">
            <button type="button" className="admin-payout-status-btn" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Got it!
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (typeof data === 'string') {
    return (
      <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
        <div className="admin-payout-status-modal" onClick={(e) => e.stopPropagation()}>
          <div className="admin-payout-status-header">
            <h2 className="admin-payout-status-title">Response Message</h2>
            <button type="button" className="admin-payout-status-close" onClick={onClose} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="admin-payout-status-content" style={{ padding: '20px' }}>
            <div className="admin-payout-status-section">
              <div className="admin-payout-status-section-header">
                <span className="section-icon">📄</span>
                <h3>Message</h3>
              </div>
              <div className="admin-payout-status-section-body">
                <p style={{ margin: 0, color: '#1f2937', fontSize: '14px' }}>{data || '—'}</p>
              </div>
            </div>
          </div>
          <div className="admin-payout-status-footer">
            <button type="button" className="admin-payout-status-btn" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Got it!
            </button>
          </div>
        </div>
      </div>
    );
  }

  const obj = data as Record<string, unknown>;
  const responseBody = (obj.response_body || obj.responseBody) as Record<string, unknown> | undefined;
  const resultStr = String(obj.result ?? obj.status ?? '');
  const statusColor = getStatusColor(resultStr);
  const topLevelKeys = Object.keys(obj).filter((k) => k !== 'response_body' && k !== 'responseBody');

  const renderDetailValue = (key: string, value: unknown): React.ReactNode => {
    if (value === null || value === undefined) return <span className="detail-value">—</span>;
    if (typeof value === 'object') return <span className="detail-value">{JSON.stringify(value)}</span>;
    const keyLower = key.toLowerCase();
    if (keyLower === 'amount') {
      const num = Number(value);
      return (
        <span className="detail-value amount">
          ₹
          {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
            isNaN(num) ? 0 : num
          )}
        </span>
      );
    }
    if (keyLower === 'response_status') return <span className="detail-value">{String(value)}</span>;
    const s = String(value);
    const isStatus = /^(SUCCESS|FAILED|PENDING|PROCESSING|COMPLETED|APPROVED|ERROR)$/i.test(s);
    if (isStatus) {
      return (
        <span className="detail-value" style={{ color: getStatusColor(s), fontWeight: 600 }}>
          {s}
        </span>
      );
    }
    const isMessage = keyLower.includes('message') || keyLower.includes('error');
    if (isMessage && /success|approved|submitted|transaction approved/i.test(s)) {
      return (
        <span className="detail-value" style={{ color: '#059669', fontWeight: 600 }}>
          {s}
        </span>
      );
    }
    return <span className="detail-value">{s}</span>;
  };

  return (
    <div className="admin-payout-status-overlay" onClick={handleBackdropClick}>
      <div className="admin-payout-status-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-payout-status-header">
          <h2 className="admin-payout-status-title">Response Message</h2>
          <button type="button" className="admin-payout-status-close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="admin-payout-status-content">
          <div className="admin-payout-status-section">
            <div className="admin-payout-status-section-header">
              <span className="status-dot" style={{ backgroundColor: statusColor }} />
              <h3>Response Summary</h3>
            </div>
            <div className="admin-payout-status-section-body">
              {resultStr && (
                <div className="status-badge-large" style={{ backgroundColor: statusColor }}>
                  {resultStr}
                </div>
              )}
              <div className="response-message-scroll-region">
                <div className="status-details-list">
                  {topLevelKeys.map((key) => (
                    <div key={key} className="status-detail-item">
                      <span className="detail-label">{formatKey(key)}:</span>
                      {key.toLowerCase() === 'result' || key.toLowerCase() === 'status' ? (
                        <span
                          className="detail-value"
                          style={{ color: getStatusColor(String(obj[key])), fontWeight: 600 }}
                        >
                          {String(obj[key])}
                        </span>
                      ) : (
                        <span className="detail-value">{String(obj[key])}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="admin-payout-status-section">
            <div className="admin-payout-status-section-header">
              <span className="section-icon">📄</span>
              <h3>Response Body</h3>
            </div>
            <div className="admin-payout-status-section-body">
              {responseBody ? (
                <div className="response-message-scroll-region">
                  <div className="status-details-list response-body-details-grid">
                    {Object.entries(responseBody).map(([key, value]) => (
                      <div key={key} className="status-detail-item">
                        <span className="detail-label">{formatKey(key)}:</span>
                        {renderDetailValue(key, value)}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="no-data-message">No response body</div>
              )}
            </div>
          </div>
        </div>

        <div className="admin-payout-status-footer">
          <button type="button" className="admin-payout-status-btn" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 12l2 2 4-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResponseMessageModal;
